import React, { useCallback, useState } from 'react';

import { Form, Link, useNavigate } from '@remix-run/react';
import { TentTypeV1 } from '@camp67/model';
import { DateField } from './date-field';
import { reservationStore } from '../../state/reservation-store';
import { parseDate } from '@camp67/model/booking/dates';
import { NumberField } from './number-field';
import { GuestList } from './guest-list';
import clsx from 'clsx';
import { PhoneField } from './phone-field';
import { PrimaryContact } from './primary-contact';
import { TextField } from './text-field';
import { SubmissionCallToAction, SubmissionCallToActionMobile } from './submission-cta';
import { useReservationStore } from '../../state/use-reservation-store';
import { FormattedString, useMessages } from '../../i18n/useMessages';
import { AmplitudeEvent, analyticsState, ButtonTag } from '../../state/amplitude';
import { DateFieldSingle } from './date-field-single';
import {
  BookingFormSectionV3,
  BookingFormSectionV3Mobile,
  Step,
} from './booking-form-section-v3';
import { TentExperienceGalleryItem } from './tent-experience-gallery';
import { TentExperienceCarousel } from './tent-experience-carousel';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface BookingFormOuterProps {
  tentType?: TentTypeV1;
}

interface BookingFormInnerProps {
  tentType: TentTypeV1;
  onClickToPayment: VoidFunction;
  onTentChange: (type: TentTypeV1, operation: 'add' | 'subtract') => void;
  onTentTypeChange: (type: TentTypeV1) => void;
}

const handleDateStartChange = (from: Date | undefined): void => {
  reservationStore.chooseDateStart(from);
};

const handleDateChange = (_from: Date | undefined, to: Date | undefined): void => {
  reservationStore.chooseDateRange(to);
};

const goToElement = (id: Step) => {
  // Delay applied because the scroll transition should occur
  // after the current step has been closed and the next step
  // has expanded
  delay(10).then(() => {
    const el = document.getElementById(id);
    if (!el) return;

    el.scrollIntoView({ behavior: 'smooth' });
  });
};
const tentTypes = Object.values(TentTypeV1);

export const BookingFormComponent: React.FC<BookingFormOuterProps> = (props) => {
  useReservationStore('camp67-booking-form');

  const onTentChange = useCallback((type: TentTypeV1, operation: 'add' | 'subtract') => {
    if (operation === 'add') {
      reservationStore.addRoom(type);
      analyticsState.amplitude?.track(AmplitudeEvent.BUTTON_CLICK, {
        buttonTag: ButtonTag.ADD_ACCOMMODATION,
        tentType: type,
      });
    } else {
      reservationStore.removeRoom(type);
      analyticsState.amplitude?.track(AmplitudeEvent.BUTTON_CLICK, {
        buttonTag: ButtonTag.SUBTRACT_ACCOMMODATION,
        tentType: type,
      });
    }
  }, []);

  const navigate = useNavigate();
  const onClickToPayment = useCallback(() => {
    const isValid = reservationStore.validate();
    if (!isValid) {
      return;
    }

    navigate('/packages/reservation/complete-payment');
  }, [navigate]);

  const [tentType, setTentType] = useState(reservationStore.getTentTypes()[0]);

  return (
    <>
      <div className="md:hidden h-full">
        <BookingFormV3Mobile
          {...props}
          tentType={tentType}
          onTentTypeChange={setTentType}
          onTentChange={onTentChange}
          onClickToPayment={onClickToPayment}
        />
      </div>
      <div className="hidden md:block h-full">
        <BookingFormV3
          {...props}
          tentType={tentType}
          onTentTypeChange={setTentType}
          onTentChange={onTentChange}
          onClickToPayment={onClickToPayment}
        />
      </div>
    </>
  );
};

export const BookingFormV3: React.FC<BookingFormInnerProps> = ({
  tentType,
  onTentChange,
  onTentTypeChange,
  onClickToPayment,
}) => {
  const reservation = reservationStore.state.reservation;
  const bookingStartDate = reservation.startDate;
  const bookingEndDate = reservation.endDate;

  const primaryContact = reservationStore.state.primaryContact;
  const hasValidGuests = reservationStore.validateGuests();
  const price = reservationStore.cost;

  const isStep1Complete = reservationStore.isStep1Complete();
  const isStep2Complete = reservationStore.isStep2Complete();

  const getMessage = useMessages();

  const [stepsExpanded, setStepsExpanded] = useState({
    'step-1': true,
    'step-2': false,
    'step-3': false,
  });

  const onExpand = useCallback(
    (step: Step) => () => {
      setStepsExpanded((prev) => ({ ...prev, [step]: !prev[step] }));
    },
    [],
  );

  const onNext = useCallback((step: Step) => {
    if (step === 'step-1') {
      setStepsExpanded((prev) => ({ ...prev, [step]: false, 'step-2': true }));
      goToElement('step-2');
    } else if (step === 'step-2') {
      setStepsExpanded((prev) => ({ ...prev, [step]: false, 'step-3': true }));
      goToElement('step-3');
    }
  }, []);

  return (
    <Form
      id="camp67-booking-form"
      className="w-full sm:pt-6 mb-36 mt-4 grid md:grid-cols-[72.5%_27.5%] gap-8 relative z-20"
    >
      <div className="w-full">
        <fieldset id="step-1" className="flex flex-col w-full mt-6 first-of-type:mt-0">
          <BookingFormSectionV3
            isStepDisabled={false}
            showExpanded={stepsExpanded['step-1']}
            step="step-1"
            messageKey="camp67-reservation.form.step-1"
            onExpand={onExpand('step-1')}
            onNext={onNext}
          >
            {tentTypes.length > 0 ? (
              <>
                <TentExperienceCarousel
                  tent={tentType}
                  countProvider={reservationStore.getRoomCount}
                  onTentChange={onTentTypeChange}
                />
                <TentExperienceGalleryItem
                  tentType={tentType}
                  currentCount={reservationStore.getRoomCount(tentType)}
                  isSelected={reservationStore.hasRoom(tentType)}
                  onClick={onTentChange}
                />
              </>
            ) : null}
          </BookingFormSectionV3>
        </fieldset>
        <fieldset id="step-2" className="flex flex-col w-full mt-6">
          <BookingFormSectionV3
            isStepDisabled={!isStep1Complete}
            showExpanded={isStep1Complete && stepsExpanded['step-2']}
            step="step-2"
            messageKey="camp67-reservation.form.step-2"
            onExpand={onExpand('step-2')}
            onNext={onNext}
          >
            <div className="flex flex-col md:flex-row relative w-full md:gap-5 px-6">
              <DateFieldSingle
                isRequired
                label="Check-in date"
                min={reservationStore.getStartOfBookingTimeRange()}
                max={reservationStore.getEndOfBookingTimeRange()}
                onChange={handleDateStartChange}
                value={bookingStartDate ? parseDate(bookingStartDate) : undefined}
                availableDates={reservationStore.getAvailableDates()}
                tents={reservationStore.state.reservation.rooms as [TentTypeV1, number][]}
                clearEndDate={(f) => handleDateChange(f, undefined)}
              />
              <DateField
                isRequired
                label="Check-out date"
                min={
                  bookingStartDate
                    ? parseDate(bookingStartDate)
                    : reservationStore.getStartOfBookingTimeRange()
                }
                max={reservationStore.getEndOfBookingTimeRange()}
                onChange={handleDateChange}
                value={{
                  from: bookingStartDate ? parseDate(bookingStartDate) : undefined,
                  to: bookingEndDate ? parseDate(bookingEndDate) : undefined,
                }}
                tents={reservationStore.state.reservation.rooms as [TentTypeV1, number][]}
                availableDates={reservationStore.getAvailableDates()}
              />
            </div>
            <div className="flex flex-col md:flex-row w-full md:gap-5 px-6">
              <NumberField
                isRequired
                type="adult"
                label={getMessage('camp67-reservation.form.step-2.adult')}
                value={reservation.guests.adult}
                onChange={reservationStore.setAdultCount}
                name="adult"
              />
              <NumberField
                type="children"
                label={getMessage('camp67-reservation.form.step-2.children')}
                detail={getMessage('camp67-reservation.form.step-2.children-detail')}
                value={reservation.guests.children}
                onChange={reservationStore.setChildCount}
                name="children"
              />
            </div>
          </BookingFormSectionV3>
        </fieldset>
        <fieldset id="step-3" className="flex flex-col w-full mt-6">
          <BookingFormSectionV3
            isStepDisabled={!isStep2Complete}
            step="step-3"
            showExpanded={isStep2Complete && stepsExpanded['step-3']}
            messageKey="camp67-reservation.form.step-3"
            onExpand={onExpand('step-3')}
            onNext={onClickToPayment}
          >
            <div className="px-6 pt-5">
              <p className="font-bold">
                {getMessage('camp67-reservation.form.step-3.guests')}
              </p>
              <p className="text-gray-200">
                <FormattedString
                  message="camp67-reservation.form.step-3.disclaimer"
                  components={{
                    'payment-policy-link': (
                      <Link
                        to="/payment-policy"
                        prefetch="intent"
                        className="underline"
                      />
                    ),
                  }}
                />
              </p>
              <GuestList
                totalTentCapacity={reservationStore.getCapacityOfRooms()}
                guestCount={reservationStore.getGuestCount()}
                guests={reservation.guests.details}
                primaryContactId={primaryContact.id}
                onGuestChange={reservationStore.updateGuestDetail}
              />
              <div
                className={clsx({
                  'opacity-50 pointer-events-none': !hasValidGuests,
                  'opacity-100': hasValidGuests,
                })}
              >
                <p className="font-bold mt-8">
                  {getMessage('camp67-reservation.form.step-3.primary-contact')}
                </p>
                <p className="text-gray-200">
                  <FormattedString
                    message="camp67-reservation.form.step-3.primary-contact.inform"
                    components={{
                      'privacy-policy-link': (
                        <Link
                          to="/privacy-policy"
                          prefetch="intent"
                          className="underline"
                        />
                      ),
                    }}
                  />
                </p>
                <PrimaryContact
                  guests={reservation.guests.details}
                  onChange={reservationStore.choosePrimaryContact}
                  primaryContact={reservationStore.state.primaryContact}
                />
                <div className="flex flex-col md:grid md:grid-cols-2 w-full md:gap-5">
                  <TextField
                    type="email"
                    isRequired
                    placeholder={getMessage(
                      'camp67-reservation.form.step-3.primary-contact.email',
                    )}
                    error={reservationStore.getError('email')}
                    onChange={reservationStore.setPrimaryEmail}
                    name="email"
                    value={primaryContact.email}
                  />
                  <PhoneField
                    name="phone"
                    onChangeCode={reservationStore.setCountryCode}
                    onChangeNumber={reservationStore.setPrimaryPhoneNumber}
                    countryCode={reservationStore.getCountryCode()}
                    phoneNumber={reservationStore.getPhoneNumber()}
                    placeholder={getMessage(
                      'camp67-reservation.form.step-3.primary-contact.phone',
                    )}
                    error={reservationStore.getError('phone')}
                  />
                </div>
              </div>
            </div>
          </BookingFormSectionV3>
        </fieldset>
      </div>
      <SubmissionCallToAction
        total={price.total}
        totalWithGst={price.totalWithGst}
        totalFromBathingDays={price.totalFromBathingDays}
        totalGst={price.totalGst}
        nights={price.nights}
        hasErrors={reservationStore.hasErrors()}
        canSubmit={reservationStore.canSubmit}
        onClick={onClickToPayment}
        reservation={reservation}
      />
    </Form>
  );
};

export const BookingFormV3Mobile: React.FC<BookingFormInnerProps> = ({
  tentType,
  onClickToPayment,
  onTentChange,
  onTentTypeChange,
}) => {
  const reservation = reservationStore.state.reservation;
  const bookingStartDate = reservation.startDate;
  const bookingEndDate = reservation.endDate;

  const primaryContact = reservationStore.state.primaryContact;
  const hasValidGuests = reservationStore.validateGuests();
  const price = reservationStore.cost;

  const isStep1Complete = reservationStore.isStep1Complete();
  const isStep2Complete = reservationStore.isStep2Complete();

  const getMessage = useMessages();

  const [stepsExpanded, setStepsExpanded] = useState({
    'step-1': true,
    'step-2': true,
    'step-3': true,
  });

  const onExpand = useCallback(
    (step: Step) => () => {
      setStepsExpanded((prev) => ({ ...prev, [step]: !prev[step] }));
    },
    [],
  );

  const onNext = useCallback((step: Step) => {
    if (step === 'step-1') {
      setStepsExpanded((prev) => ({ ...prev, [step]: false, 'step-2': true }));
      goToElement('step-2');
    } else if (step === 'step-2') {
      setStepsExpanded((prev) => ({ ...prev, [step]: false, 'step-3': true }));
      goToElement('step-3');
    }
  }, []);

  return (
    <Form id="camp67-booking-form" className="w-full h-full gap-8 relative z-20">
      <div className="w-full">
        <fieldset id="step-1" className="flex flex-col w-full">
          <BookingFormSectionV3Mobile
            isStepDisabled={false}
            showExpanded={stepsExpanded['step-1']}
            step="step-1"
            messageKey="camp67-reservation.form.step-1"
            onExpand={onExpand('step-1')}
            onNext={onNext}
          >
            {tentTypes.length > 0 ? (
              <>
                <TentExperienceCarousel
                  tent={tentType}
                  countProvider={reservationStore.getRoomCount}
                  onTentChange={onTentTypeChange}
                />
                <TentExperienceGalleryItem
                  tentType={tentType}
                  currentCount={reservationStore.getRoomCount(tentType)}
                  isSelected={reservationStore.hasRoom(tentType)}
                  onClick={onTentChange}
                />
              </>
            ) : null}
          </BookingFormSectionV3Mobile>
        </fieldset>
        <fieldset id="step-2" className="flex flex-col w-full relative z-30">
          <BookingFormSectionV3Mobile
            isStepDisabled={!isStep1Complete}
            showExpanded={isStep1Complete && stepsExpanded['step-2']}
            step="step-2"
            messageKey="camp67-reservation.form.step-2"
            onExpand={onExpand('step-2')}
            onNext={onNext}
          >
            <div className="flex flex-col w-full px-6 relative z-30">
              <DateFieldSingle
                isRequired
                isMobile
                label="Check-in date"
                min={reservationStore.getStartOfBookingTimeRange()}
                max={reservationStore.getEndOfBookingTimeRange()}
                onChange={handleDateStartChange}
                value={bookingStartDate ? parseDate(bookingStartDate) : undefined}
                availableDates={reservationStore.getAvailableDates()}
                tents={reservationStore.state.reservation.rooms as [TentTypeV1, number][]}
                clearEndDate={(f) => handleDateChange(f, undefined)}
              />
              <DateField
                isRequired
                isMobile
                label="Check-out date"
                min={
                  bookingStartDate
                    ? parseDate(bookingStartDate)
                    : reservationStore.getStartOfBookingTimeRange()
                }
                max={reservationStore.getEndOfBookingTimeRange()}
                onChange={handleDateChange}
                value={{
                  from: bookingStartDate ? parseDate(bookingStartDate) : undefined,
                  to: bookingEndDate ? parseDate(bookingEndDate) : undefined,
                }}
                tents={reservationStore.state.reservation.rooms as [TentTypeV1, number][]}
                availableDates={reservationStore.getAvailableDates()}
              />
            </div>
            <div className="flex flex-col w-full px-6">
              <NumberField
                isRequired
                type="adult"
                label={getMessage('camp67-reservation.form.step-2.adult')}
                value={reservation.guests.adult}
                onChange={reservationStore.setAdultCount}
                name="adult"
              />
              <NumberField
                type="children"
                label={getMessage('camp67-reservation.form.step-2.children')}
                detail={getMessage('camp67-reservation.form.step-2.children-detail')}
                value={reservation.guests.children}
                onChange={reservationStore.setChildCount}
                name="children"
              />
            </div>
          </BookingFormSectionV3Mobile>
        </fieldset>
        <fieldset id="step-3" className="flex flex-col w-full pb-12">
          <BookingFormSectionV3Mobile
            isStepDisabled={!isStep2Complete}
            step="step-3"
            showExpanded={isStep2Complete && stepsExpanded['step-3']}
            messageKey="camp67-reservation.form.step-3"
            onExpand={onExpand('step-3')}
            onNext={onClickToPayment}
          >
            <div className="px-6 pt-5">
              <p className="font-bold">
                {getMessage('camp67-reservation.form.step-3.guests')}
              </p>
              <p className="text-gray-200">
                <FormattedString
                  message="camp67-reservation.form.step-3.disclaimer"
                  components={{
                    'payment-policy-link': (
                      <Link
                        to="/payment-policy"
                        prefetch="intent"
                        className="underline"
                      />
                    ),
                  }}
                />
              </p>
              <GuestList
                totalTentCapacity={reservationStore.getCapacityOfRooms()}
                guestCount={reservationStore.getGuestCount()}
                guests={reservation.guests.details}
                primaryContactId={primaryContact.id}
                onGuestChange={reservationStore.updateGuestDetail}
              />
              <div
                className={clsx({
                  'opacity-50 pointer-events-none': !hasValidGuests,
                  'opacity-100': hasValidGuests,
                })}
              >
                <p className="font-bold mt-8">
                  {getMessage('camp67-reservation.form.step-3.primary-contact')}
                </p>
                <p className="text-gray-200">
                  <FormattedString
                    message="camp67-reservation.form.step-3.primary-contact.inform"
                    components={{
                      'privacy-policy-link': (
                        <Link
                          to="/privacy-policy"
                          prefetch="intent"
                          className="underline"
                        />
                      ),
                    }}
                  />
                </p>
                <PrimaryContact
                  guests={reservation.guests.details}
                  onChange={reservationStore.choosePrimaryContact}
                  primaryContact={reservationStore.state.primaryContact}
                />
                <div className="flex flex-col w-full">
                  <TextField
                    type="email"
                    isRequired
                    placeholder={getMessage(
                      'camp67-reservation.form.step-3.primary-contact.email',
                    )}
                    error={reservationStore.getError('email')}
                    onChange={reservationStore.setPrimaryEmail}
                    name="email"
                    value={primaryContact.email}
                  />
                  <PhoneField
                    name="phone"
                    onChangeCode={reservationStore.setCountryCode}
                    onChangeNumber={reservationStore.setPrimaryPhoneNumber}
                    countryCode={reservationStore.getCountryCode()}
                    phoneNumber={reservationStore.getPhoneNumber()}
                    placeholder={getMessage(
                      'camp67-reservation.form.step-3.primary-contact.phone',
                    )}
                    error={reservationStore.getError('phone')}
                  />
                </div>
              </div>
            </div>
          </BookingFormSectionV3Mobile>
        </fieldset>
      </div>
      <SubmissionCallToActionMobile
        total={price.total}
        totalWithGst={price.totalWithGst}
        totalFromBathingDays={price.totalFromBathingDays}
        totalGst={price.totalGst}
        nights={price.nights}
        hasErrors={reservationStore.hasErrors()}
        canSubmit={reservationStore.canSubmit}
        onClick={onClickToPayment}
        reservation={reservation}
      />
    </Form>
  );
};
